import React, { useEffect, useRef, useState } from "react"

import useSmartCrop from "@/hooks/useSmartCrop"
import useIsSsr from "@/hooks/useIsSsr"
import {
  appendImageModifiers,
  getManualSmartCropFallbackUrl,
  getManualSmartCropSrcSet,
  getSortedManualSmartCropItems,
  isManualSmartCropEnabled,
} from "@/components/core/Image/manualSmartCrop"
const DynamicImage = ({
  imgUrl = "",
  alt = "",
  className = "",
  uniqueId = "",
  urlModifiers = "",
  pageType = "",
  enableManualSmartCrop = false,
  smartCropItems = [],
  manualSmartCropBaseUrl = "",
  defaultImageURL = "",
  defaultRendition = "",
  imageModifiersScene7 = "",
  ...restProps
}) => {
  const isManualSmartCrop = isManualSmartCropEnabled(enableManualSmartCrop)
  const imgPlaceholder =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
  const manualCropBaseUrl = manualSmartCropBaseUrl || imgUrl
  const validSmartCropItems = getSortedManualSmartCropItems(
    smartCropItems,
    manualCropBaseUrl
  )
  const manualImageFallback = getManualSmartCropFallbackUrl({
    defaultImageURL,
    defaultRendition,
    baseUrl: manualCropBaseUrl !== imgPlaceholder ? manualCropBaseUrl : "",
    sortedItems: validSmartCropItems,
  })
  const hasManualSmartCrops =
    isManualSmartCrop &&
    Boolean(manualImageFallback) &&
    validSmartCropItems.length > 0
  const manualPictureFallback =
    (defaultRendition &&
      manualCropBaseUrl &&
      `${manualCropBaseUrl}:${defaultRendition}`) ||
    validSmartCropItems[validSmartCropItems.length - 1]?.imageURL ||
    manualImageFallback
  const smartCrop = useSmartCrop(hasManualSmartCrops)
  const isSsr = useIsSsr()
  const cropedImage = useRef(null)
  const isScene7Img = imgUrl?.includes("scene7")
  const [bgColorImage, setBgColorImage] = useState("")

  useEffect(() => {
    if (
      !isSsr &&
      smartCrop.scriptStatus &&
      !hasManualSmartCrops &&
      isScene7Img &&
      typeof window !== "undefined" &&
      window.s7responsiveImage
    ) {
      smartCrop.applySmartCrop(cropedImage.current)
    }
  }, [smartCrop.scriptStatus, imgUrl, uniqueId, isSsr, hasManualSmartCrops])

  useEffect(() => {
    if (urlModifiers && imgUrl) {
      setBgColorImage(
        imgUrl?.includes("?")
          ? `${imgUrl}&${urlModifiers}`
          : `${imgUrl}?${urlModifiers}`
      )
    }
  }, [imgUrl, urlModifiers])

  const isPLP = pageType === "plp"
  const imageModifiers = imageModifiersScene7 || urlModifiers

  if (hasManualSmartCrops) {
    return (
      <picture>
        {validSmartCropItems.map((item, index) => (
          <source
            key={`${item.deviceSize}-${index}`}
            media={`(min-width: ${item.deviceSize})`}
            srcSet={getManualSmartCropSrcSet(item.imageURL, {
              modifiers: imageModifiers,
              deviceSize: item.deviceSize,
            })}
          />
        ))}
        <img
          className={className}
          key={uniqueId}
          alt={alt}
          src={appendImageModifiers(manualPictureFallback, imageModifiers)}
          srcSet={getManualSmartCropSrcSet(manualPictureFallback, {
            modifiers: imageModifiers,
            deviceSize:
              validSmartCropItems[validSmartCropItems.length - 1]?.deviceSize,
          })}
          {...restProps}
        />
      </picture>
    )
  }

  return (
    <img
      ref={cropedImage}
      data-src={urlModifiers ? bgColorImage : imgUrl}
      data-mode="smartcrop"
      className={className}
      key={uniqueId}
      alt={alt}
      {...(!isPLP && {
        src: imgUrl || imgPlaceholder,
      })}
      {...restProps}
    />
  )
}

export default DynamicImage
